/*
  collapseAllSingleParentNodes.js
*/

/**
 * Collapses all nodes in graphData that are the sole parent of child nodes.
 * This is probably the neatest way to collapse the graph.
 * 
 * NOTE: This algorithm could be quite slow. It's at least N^2.
 * Might be able to optimize with memoisation.
 */
export default function collapseAllSingleParentNodes(graphData) {

  // Get a map of all source -> target edges, as well as target -> source edges.
  const sourceTargetMap = new Map();
  const targetSourceMap = new Map();
  for (const link of graphData.links) {

    // Source -> target.
    const tSet = sourceTargetMap.get(link.source) || new Set();
    tSet.add(link.target);
    sourceTargetMap.set(link.source, tSet);

    // Target -> source.
    const sSet = targetSourceMap.get(link.target) || new Set();
    sSet.add(link.source);
    targetSourceMap.set(link.target, sSet);
  }

  // Determine which nodes should collapse.
  const replaceNodes = new Map();
  for (const rootNode of graphData.nodes) {

    // If this node is already collapsed, ignore.
    if (rootNode._botech?.collapsed) {
      continue;
    }

    // Gather up all child nodes.
    // If a child ends up back at the root node, that child is part of a cycle, so we ignore all of it.
    // NOTE: If a child node is already collapsed, then we skip that child, because it was collapsed
    // forcefully with parents included and can now be treated as a single child with no further children.
    function visit(node, visited) {
      if (!visited.has(node) && !replaceNodes.has(node) && !node._botech?.collapsed) {
        visited.add(node);
        const children = sourceTargetMap.get(node);
        if (children) {
          if (children.has(rootNode)) {
            return true;  // Cycle detected. End.
          }
          for (const child of children) {
            if (visit(child, visited)) {
              return true;  // Cycle detected. End.
            }
          }
        }
      }
      return false;
    }

    // Iterate through root children, and gather up all visited nodes.
    // If a cycle is found, cancel early.
    let cycleFound = false;
    const allVisited = new Set();
    const children = sourceTargetMap.get(rootNode);
    if (children) {
      for (const child of children) {
        const visited = new Set();
        if (visit(child, visited)) {
          cycleFound = true;
          break;
        } else {
          for (const nd of visited) {
            allVisited.add(nd);
          }
        }
      }
    }

    // If there were no children found, there is no point collapsing this.
    if (cycleFound || allVisited.size < 1) {
      continue;
    }

    // Check if any of them have a parent outside of the overall set (ignoring the root "node").
    // If they do, then this node can't be collapsed.
    let outside = false;
    for (const node of allVisited) {
      const parents = targetSourceMap.get(node);
      if (parents) {
        for (const parent of parents) {
          if (parent !== rootNode && !allVisited.has(parent)) {
            outside = true;
            break;
          }
        }
        if (outside) {
          break;
        }
      }
    }
    if (!outside) {
      replaceNodes.set(rootNode, { ...rootNode, _botech: { ...rootNode._botech, collapsed: true } }); 
    }
  }

  // If there are no replacements, no updates are required.
  if (replaceNodes.size === 0) {
    return graphData;
  }

  // Update graph data with the node replacements.
  return {
    ...graphData,
    nodes: graphData.nodes.map(obj => replaceNodes.get(obj) || obj),
    links: graphData.links.map(obj => replaceNodes.has(obj.source) || replaceNodes.has(obj.target) ? {
      ...obj,
      source: replaceNodes.get(obj.source) || obj.source,
      target: replaceNodes.get(obj.target) || obj.target,
    } : obj),
  };
}
